<template>
  <main id="app" :class="{ 'new-menu': hasToShowNewMenu }">
    <menu-global
      v-if="hasToShowNewMenu"
      :cdn_url="legacyCdnUrl"
      :painel_domain="painelDomain"
    ></menu-global>
    <menu-sidebar
      v-if="hasToShowNewMenu && featureWeddingServices"
      :cdn_url="legacyCdnUrl"
      :advisors_domain="advisorsDomain"
      :feature_wedding_services="true"
      :feature_new_stationary="featureNewStationary"
      :new_stationary_url="papelaria_url"
    ></menu-sidebar>
    <menu-sidebar
      v-else-if="hasToShowNewMenu && !featureWeddingServices"
      :cdn_url="legacyCdnUrl"
      :advisors_domain="advisorsDomain"
      :feature_new_stationary="featureNewStationary"
      :new_stationary_url="papelaria_url"
    ></menu-sidebar>
    <menu-tour
      v-if="hasToShowNewMenu"
      :cdn_url="legacyCdnUrl"
      :painel_domain="painelDomain"
    ></menu-tour>
    <router-view v-if="showPage" :key="$route.path" />
    <transition name="fade">
      <BaseWebpageLoading v-if="loading" />
    </transition>
    <ShareWedsiteUrlModal ref="shareWedsiteUrlModal" />
    <ShareQRCodeModal ref="shareQRCodeModal" />
    <ShareMyAdvisorModal ref="shareMyAdvisorModal" />
  </main>
</template>

<script>
import Buefy from 'buefy'
import 'buefy/dist/buefy.css'
import Vue from 'vue'
import { mapActions, mapGetters } from 'vuex'

import BaseWebpageLoading from '@/components/BaseWebpageLoading.vue'
import ShareMyAdvisorModal from '@/components/Shared/ShareMyAdvisorModal.vue'
import ShareQRCodeModal from '@/components/Shared/ShareQRCodeModal.vue'
import ShareWedsiteUrlModal from '@/components/Shared/ShareWedsiteUrlModal.vue'
import { getLinkFromRoute, getActiveMenu } from '@/router/menu'
import { getUrlWithoutProtocol } from '@/lib/utils/TextHelper'
import { fetchNewStationeryToggle } from '@/lib/newStationeryToggle'
import WalletLegacyApi from '@/lib/api/WalletLegacy'
import GiftsApi from '@/lib/api/Gifts'
import WeddingServicesApi from '@/lib/api/WeddingServices'
import loadingViewMixin from '@/mixins/loadingViewMixin'
import isMobileBrowser from '@/plugins/detect-mobile-browser'
import NotificationAlerts from './lib/api/NotificationAlerts'

Vue.use(Buefy, {
  defaultIconPack: 'fas',
})

const SITE_URL = process.env.VUE_APP_SITE_URL

export default {
  name: 'App',

  mixins: [loadingViewMixin],

  metaInfo: {
    title: 'Área logada',
    titleTemplate: '%s - Casar.com',
  },

  components: {
    BaseWebpageLoading,
    ShareWedsiteUrlModal,
    ShareQRCodeModal,
    ShareMyAdvisorModal,
  },

  data() {
    return {
      legacyCdnUrl: process.env.VUE_APP_CDN_LEGACY_ASSETS_URL,
      painelDomain: process.env.VUE_APP_SITE_URL.replace('www', 'painel'),
      advisorsDomain: process.env.VUE_APP_SITE_URL.replace(
        'www',
        'assessorias'
      ),
      featureWeddingServices: false,
      featureNewStationary: false,
      papelaria_url: process.env.VUE_APP_PAPELARIA_URL || '',
    }
  },

  computed: {
    ...mapGetters({
      logonToken: 'app/logonToken',
      wedSiteInfo: 'app/getWedSiteInfo',
      walletDetails: 'getWalletDetails',
    }),

    logonTokenBearer() {
      return this.logonToken?.token
    },

    showPage() {
      if (this.isPublicRoute && !this.loading) return true
      if (this.logonTokenBearer && !this.loading) return true
      return false
    },

    isPublicRoute() {
      const publicRouteNames = [
        'shopping-confirmar-senha',
        'convites-modelos',
        'convites-layouts',
        'convites',
        'pix-validacao-email',
        'pix-validacao-email-path',
        'pix-brcode-copied',
        'rsvp-convites',
      ]

      return publicRouteNames.includes(this.$route.name)
    },

    hasToShowNewMenu() {
      const { name } = this.$route
      const { site_menu_versao } = this.wedSiteInfo

      const isOnboardingRoute = name?.startsWith('onboarding')
      const isCustomizationRoute = name === 'customizacao-do-tema'
      const isRsvpRoute = name === 'rsvp-convites'

      return (
        site_menu_versao &&
        !isOnboardingRoute &&
        !isCustomizationRoute &&
        !isRsvpRoute
      )
    },
  },

  mounted() {
    const routeName = this.$route.name
    if (routeName !== 'shopping-confirmar-senha') {
      this.initApp()
    } else {
      this.loading = false
    }
  },

  methods: {
    ...mapActions({
      fetchLogonToken: 'app/fetchLogonToken',
      fetchWedSiteInfo: 'app/fetchWedSiteInfo',
      fetchNewRepassTimeEnabled: 'app/fetchNewRepassTimeEnabled',
      setWalletDetails: 'setWalletDetails',
      walletDetails: 'getWalletDetails',
    }),

    async initApp() {
      await this.fetchLogonToken()
      const withoutToken = this.redirectWithoutToken()
      if (withoutToken) return

      const weddingServicesApi = new WeddingServicesApi(this.logonToken)
      this.featureWeddingServices =
        await weddingServicesApi.getIsWeddingSiteMigrated()

      await this.fetchNewStationary()

      await this.fetchWedSiteInfo()
      await this.fetchNewRepassTimeEnabled()

      const walletLegacyApi = new WalletLegacyApi(this.logonToken)
      const walletDetails = await walletLegacyApi.getWalletDetails()
      this.setWalletDetails(walletDetails?.data)

      this.initMenu()
      this.loading = false

      this.$nextTick(() => {
        const customEvent = new Event('SHAREDMENU_ACTIVE_ROUTE')
        customEvent.active = getActiveMenu(this.$route.path)
        window.dispatchEvent(customEvent)
      })
    },

    redirectWithoutToken() {
      if (!this.logonTokenBearer && this.isPublicRoute) {
        this.loading = false
        return true
      }

      if (!this.logonTokenBearer) {
        const firstSlashReg = new RegExp(/^\//)

        location.href = SITE_URL + 'entrar?go=' + this.$route.fullPath.replace(firstSlashReg, '')
        return true
      }

      return false
    },

    async fetchNewStationary() {
      const wedId = this.$store.getters['app/getWedId']
      this.featureNewStationary = await fetchNewStationeryToggle(wedId)
    },

    saveWhenAlertOfWalletOwnerExhibitedIfWasntSavedBefore(alerts, type, api) {
      const viewed = alerts.filter(({ resposta }) => resposta === 'visualizado')

      if (viewed.length === 0) {
        api.saveNotificationAlerts({
          tipo: type,
          resposta: 'visualizado',
        })
      }
    },

    saveWhenAlertOfWalletOwnerStoppedExhibitionIfWasntSavedBefore(
      alerts,
      type,
      api
    ) {
      const viewed = alerts.filter(({ resposta }) => resposta === 'visualizado')
      const stopped = alerts.filter(({ resposta }) => resposta === 'parou')

      if (viewed.length === 1 && stopped.length === 0) {
        api.saveNotificationAlerts({
          tipo: type,
          resposta: 'parou',
        })
      }
    },

    async showAlertOfWalletOwnerUnregistered() {
      const type = 'alert_wallet_owner'
      const giftsApi = new GiftsApi(this.logonToken)
      const giftsSummary = await giftsApi.getGiftsSummary()
      const notificationAlertsApi = new NotificationAlerts(this.logonToken)

      const alerts = await notificationAlertsApi.getNotificationAlertsByType(
        type
      )

      if (!giftsSummary.data || giftsSummary.data.gifts_count <= 0) return false

      if (!this.walletDetails || this.walletDetails?.has_wallet_owner) {
        this.saveWhenAlertOfWalletOwnerStoppedExhibitionIfWasntSavedBefore(
          alerts,
          type,
          notificationAlertsApi
        )
        return false
      }

      this.saveWhenAlertOfWalletOwnerExhibitedIfWasntSavedBefore(
        alerts,
        type,
        notificationAlertsApi
      )
      return true
    },

    async initMenu() {
      if (this.hasToShowNewMenu) {
        const that = this
        const $router = this.$router
        window.addEventListener('SHAREDMENU_LINK_CLICKED', function (event) {
          const to = event.to
          if (to == 'share-wedsite') {
            that.openWedsiteSharingModal()
            return
          } else if (to == 'share-qrcode') {
            that.openQRCodeModal()
            return
          } else if (to == 'clipboard-wedsite') {
            that
              .$copyText(
                getUrlWithoutProtocol(
                  that.wedSiteInfo.shared_menu.wedsite_url_for_guests
                )
              )
              .then(function () {
                window.dispatchEvent(new Event('SHAREDMENU_WEDSITE_URL_COPIED'))
              })
            return
          } else if (to == 'share-my-advisor') {
            that.openShareMyAdvisorModal() // openModal
            return
          }

          let link = getLinkFromRoute(event.to, that.wedSiteInfo)
          if (link.type === 'route') {
            $router.push(link.dest)
          } else if (link.type === 'link-wedsite') {
            window.location.href = that.wedSiteInfo.site_url + '/' + link.dest
          } else if (link.type === 'link-legacy') {
            window.location.href =
              process.env.VUE_APP_LEGACY_ADMIN_URL + link.dest
          }
        })

        setTimeout(async () => {
          const customEvent = new Event('SHAREDMENU_LOADED')
          for (let prop in that.wedSiteInfo.shared_menu) {
            customEvent[prop] = that.wedSiteInfo.shared_menu[prop]
          }
          customEvent._is_mobile = isMobileBrowser()
          customEvent.alert_wallet_owner_unregistered =
            await this.showAlertOfWalletOwnerUnregistered()

          window.dispatchEvent(customEvent)
        }, 250)
      }
    },

    openWedsiteSharingModal() {
      this.$refs.shareWedsiteUrlModal.open()
    },

    openQRCodeModal() {
      this.$refs.shareQRCodeModal.open()
    },
    // add modal shared my advisor
    openShareMyAdvisorModal() {
      this.$refs.shareMyAdvisorModal.open()
    },
  },
}
</script>

<style lang="scss">
@import './assets/scss/bit-assets/styles.scss';
</style>
